<script setup>

import {defineProps, defineEmits, onMounted, ref} from "vue";
import VerticalModal from "@/components/base/VerticalModal.vue";
import BaseIcon from "@/components/base/BaseIcon.vue";
import NewBtn from "@/components/base/btn/NewBtn.vue";
import {apiGetLoad} from "@/services/orders/LoadApis";
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue";


const props = defineProps({
  modelValue: { type: Boolean, default: false },
  load: { type: Object, default: null },
});

const loading = ref(false)
const loadData = ref(null)
const emit = defineEmits(["update:modelValue", "close"]);

// Close
const closed = () => {
  emit('update:modelValue', false)
  emit('close', true)
}

onMounted(() => {
  if(!props.load) getLoad()
})


const getLoad = async () => {
  try {
    loading.value = true
    const response = await apiGetLoad()

    if(!response) return
    loadData.value = response.data?.load || null

  } finally {
    loading.value = false
  }
}
</script>

<template>

  <vertical-modal
    :visible="modelValue"
    @close="closed"
    body-class="tw-py-8 tw-px-6 tw-bg-gray-300"
    footer-class="tw-justify-between"
    width="md">

    <template #body>
      <div v-if="loading" class="tw-w-[595px] tw-h-[80%] tw-text-center tw-p-20 tw-mx-auto tw-bg-white tw-rounded tw-overflow-y-auto tw-no-scrollbar tw-overscroll-none">
        <LoadingSpinner size="lg" :show="loading" color="secondary" class="tw-m-auto" />
      </div>
      <div class="tw-w-full tw-h-full tw-bg-gray-300 tw-py-4" v-else-if="load">
        <div
          ref="printableContent"
          class="tw-w-[595px] tw-h-[calc(100%_-_89px)] tw-flex tw-flex-col tw-gap-[10px] tw-p-4 tw-mx-auto tw-bg-white tw-rounded tw-overflow-y-auto tw-no-scrollbar tw-overscroll-none"
        >
          <div class="tw-rounded-lg tw-border-[1px] tw-border-blue-dark-500">
            <!-- Header -->
            <div class="tw-flex tw-items-center tw-justify-between tw-pt-4 tw-pb-3 tw-px-3 tw-text-blue-dark-500 tw-border-b-[1px] tw-border-blue-dark-500">
              <img src="~@/assets/images/ayandehcement-logo.png" alt="logo" class="tw-w-[128px] tw-h-auto" />
              <div class="tw-text-xs tw-text-center">
                <div>((معرفی نامه کارخانه))</div>
              </div>
              <div class="tw-text-sm">
                <div class="tw-flex tw-items-center tw-font-bold">
                  <BaseIcon icon="barcode" class="tw-text-xl" />
                  <div class="mr-2">شناسه بار {{ loadData?.load.id }}</div>
                </div>
                <div class="tw-flex tw-items-center tw-mt-2">
                  <BaseIcon icon="event" class="tw-text-xl" />
                  <div class="tw-mr-2">
                    {{
                      !isJalali(loadData?.load.date_number_bar)
                        ? convertUtcToPersianDate(loadData?.load.date_number_bar)
                        : loadData?.load.date_number_bar
                    }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex gap-3 pt-3 pb-4 px-3 text-blue-dark-500 border-b-[1px] border-blue-dark-500">
              <div class="flex flex-col gap-2 pl-6 border-l-[1px] border-blue-dark-500">
                <div class="text-xs">مشخصات محصول</div>
                <div class="text-xs font-bold">{{ loadData?.load.product.title }}</div>
              </div>
              <div class="flex flex-col gap-1 pl-4 border-l-[1px] border-blue-dark-500">
                <div class="text-xs">حداکثر تناژ مجاز</div>
                <div class="flex items-center gap-2">
                  <BaseIcon icon="weight" class="!text-base" />
                  <div class="text-xs">{{ loadData?.load.max_ton_loaded }} تن</div>
                </div>
              </div>
              <div class="flex flex-col gap-1 pl-4">
                <div class="text-xs">شماره بارنامه</div>
                <div class="flex items-center gap-2">
                  <BaseIcon icon="info" class="!text-base" />
                  <div class="text-xs">{{ numberBar && serialNumberBar ? `${numberBar}/${serialNumberBar}` : "بدون بارنامه" }}</div>
                </div>
              </div>
              <div class="w-[160px] h-10 flex items-center border border-gray-200 bg-white rounded">
                <div class="w-[22%] h-10 flex items-center justify-center border-l border-gray-200 font-bold text-blue-dark-500">
                  <span>{{ loadData?.load.car.car_number_base }}</span>
                </div>
                <div class="w-[56%] h-10 flex items-center justify-center gap-x-2.5 font-bold text-blue-dark-500">
                <span>
                  {{ loadData?.load.car.car_number_three_digits }}
                </span>
                  <span>
                  {{ loadData?.load.car.car_number_letter }}
                </span>
                  <span>
                  {{ loadData?.load.car.car_number_two_digits }}
                </span>
                </div>
                <div
                  class="w-[22%] h-10 flex flex-col items-end bg-[#00008B] pl-[6px] pt-[5px] text-white text-[9px] rounded-l border-r border-gray-200"
                >
                  <img src="~@/assets/images/iran_flag.svg" alt="iran natinal flag" width="15px" height="10px" />
                  <span class="">I.R</span>
                  <span class="mt-[-2px]">IRAN</span>
                </div>
              </div>
            </div>
            <div class="pt-6 pb-5 px-4">
              <div class="flex justify-between gap-3">
                <div class="text-xs text-blue-dark-500">
                  <div>با سلام و احترام</div>
                  <div class="mt-2">
                    کامیون با مشخصات اعلام شده جهت بارگیری بار <span class="text-sm font-bold"> شهر {{ loadData?.load.city.title }} </span> معرفی
                    می‌گردد.
                  </div>
                  <div class="mt-1">از همکاری شما سپاس گذاریم.</div>
                </div>
                <div>
                  <img src="~@/assets/images/intro-stamp.svg" alt="" />
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center">
            <BaseIcon icon="content-cut" class="rotate-180 text-base" />
            <div class="w-full h-px border-dashed border-t-[1px] border-blue-dark-100"></div>
          </div>
          <div class="rounded-lg border-[1px] border-blue-dark-500">
            <!-- Header -->
            <div class="flex items-center justify-between pt-4 pb-3 px-3 text-blue-dark-500 border-b-[1px] border-blue-dark-500">
              <img src="~@/assets/images/ayandehcement-logo.png" alt="logo" class="w-[128px] h-auto" />
              <div class="text-xs text-center">
                <!-- <div class="mb-2 font-bold">بسمه تعالی</div> -->
                <div>((رسید گیرنده))</div>
              </div>
              <div class="text-sm">
                <div class="flex items-center font-bold">
                  <BaseIcon icon="barcode" class="text-xl" />
                  <div class="mr-2">شناسه بار {{ loadData?.load.id }}</div>
                </div>
                <div class="flex items-center mt-2">
                  <BaseIcon icon="event" class="text-xl" />
                  <div class="mr-2">
                    {{
                      !isJalali(loadData?.load?.date_number_bar)
                        ? convertUtcToPersianDate(loadData?.load.date_number_bar)
                        : loadData?.load.date_number_bar
                    }}
                  </div>
                </div>
              </div>
            </div>

            <div class="flex items-center gap-5 pt-3 pb-4 px-5 text-blue-dark-500 border-b-[1px] border-blue-dark-500">
              <div class="flex flex-col gap-2 pl-6 border-l-[1px] border-blue-dark-500">
                <div class="text-xs">مشخصات محصول</div>
                <div class="text-xs font-bold">{{ loadData?.load.product.title }}</div>
              </div>
              <div class="flex flex-col gap-2 pl-6 border-l-[1px] border-blue-dark-500">
                <div class="text-xs">کرایه {{ loadData?.load.type_rent_title }}</div>
                <div class="flex items-center gap-2 px-2 py-1 bg-gradient-gray rounded">
                  <BaseIcon icon="money-bag" class="text-xl" />
                  <span class="text-xs">
                  {{
                      loadData?.load?.type_rent === "proposaly"
                        ? new Intl.NumberFormat().format(Number(loadData?.load?.proposal_rent || 0))
                        : new Intl.NumberFormat().format(loadData?.load?.agreement_rent || 0)
                    }}
                  ریال
                </span>
                </div>
              </div>
              <div class="w-[160px] h-10 flex items-center border border-gray-200 bg-white rounded">
                <div class="w-[22%] h-10 flex items-center justify-center border-l border-gray-200 font-bold text-blue-dark-500">
                  <span>{{ loadData?.load.car.car_number_base }}</span>
                </div>
                <div class="w-[56%] h-10 flex items-center justify-center gap-x-2.5 font-bold text-blue-dark-500">
                <span>
                  {{ loadData?.load.car.car_number_three_digits }}
                </span>
                  <span>
                  {{ loadData?.load.car.car_number_letter }}
                </span>
                  <span>
                  {{ loadData?.load.car.car_number_two_digits }}
                </span>
                </div>
                <div
                  class="w-[22%] h-10 flex flex-col items-end bg-[#00008B] pl-[6px] pt-[5px] text-white text-[9px] rounded-l border-r border-gray-200"
                >
                  <img src="~@/assets/images/iran_flag.svg" alt="iran natinal flag" width="15px" height="10px" />
                  <span class="">I.R</span>
                  <span class="mt-[-2px]">IRAN</span>
                </div>
              </div>
            </div>
            <div class="flex flex-col gap-5 pt-6 pb-5 px-4">
              <div class="w-full flex gap-3">
                <div class="w-1/2 flex flex-col gap-2 text-blue-dark-500">
                  <span class="text-xs">اطلاعات گیرنده</span>
                  <div class="flex items-center gap-2">
                    <BaseIcon icon="person" />
                    <span class="text-sm font-bold">{{ loadData?.load.user.first_name + " " + loadData?.load.user.last_name }}</span>
                  </div>
                  <div class="w-full px-2 py-1 rounded bg-gradient-gray text-sm">{{ loadData?.load.user.mobile ?? "-" }}</div>
                </div>
                <div class="w-1/2 flex flex-col gap-2 text-blue-dark-500">
                  <span class="text-xs">اطلاعات راننده</span>
                  <div class="flex items-center gap-2">
                    <BaseIcon icon="id-card" />
                    <span class="text-sm font-bold">{{ loadData?.load.driver?.first_name + " " + loadData?.load.driver?.last_name }}</span>
                  </div>
                  <div class="w-full px-2 py-1 rounded bg-gradient-gray text-sm">
                    {{ `${loadData?.load.driver.mobile} - ${loadData?.load.car?.car_type?.title ?? ""}` }}
                  </div>
                </div>
              </div>
              <div class="w-full flex gap-3">
                <div class="w-1/2 flex flex-col gap-2 text-blue-dark-500">
                  <span class="text-xs">آدرس تحویل</span>
                  <div class="flex gap-2">
                    <BaseIcon icon="location" />
                    <span class="text-sm font-bold">{{ loadData?.load.address }}</span>
                  </div>
                  <div class="w-full px-2 py-1 rounded bg-gradient-gray text-sm">
                    {{ `شماره بارنامه ${numberBar && serialNumberBar ? numberBar + "/" + serialNumberBar : "بدون بارنامه"}` }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="tw-flex tw-items-center tw-gap-2">
        <BaseIcon icon="campaign" class="tw-text-secondry-600" />
        <span class="text-sm text-blue-dark-500">همراه داشتن پرینت این معرفی نامه جهت بارگیری در کارخانه الزامی است.</span>
      </div>

      <div class="max-[395px]:tw-w-full tw-text-left tw-flex tw-gap-3">
        <NewBtn color="primary" @click="closed">بستن</NewBtn>
        <NewBtn color="primary" icon="print" label="پرینت" />
      </div>
    </template>
  </vertical-modal>
</template>

<style scoped>

</style>
